import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Users, BarChart3, Zap, Globe, TrendingUp, Calendar, Plus, Eye, Settings, FileText } from 'lucide-react';

interface AnalyticsData {
  overview: {
    clients: { total: number; recent: number };
    campaigns: { total: number; active: number };
    content: { total: number; recent: number };
    syndication: { jobs: number; published: number };
  };
  campaign_performance: Array<{
    id: string;
    business_name: string;
    type: string;
    total_posts: number;
    published_posts: number;
    syndication_jobs: number;
    completed_syndications: number;
  }>;
  content_trends: Array<{
    date: string;
    count: number;
  }>;
}

const AdminDashboard: React.FC = () => {
  const { user, agency, logout, token } = useAuth();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (token) {
      fetchAnalytics();
    }
  }, [token]);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/analytics/overview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAnalytics(result.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = () => {
    // TODO: Open campaign creation modal
    alert('Campaign creation modal would open here');
  };

  const handleGenerateContent = () => {
    // TODO: Open content generation modal
    alert('Content generation modal would open here');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold gradient-text">BipSEO</h1>
              <span className="ml-4 text-gray-400">|</span>
              <span className="ml-4 text-lg font-semibold text-gray-700">
                {agency?.name}
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Welcome, {user?.name || user?.email}
              </span>
              <button
                onClick={logout}
                className="text-gray-500 hover:text-gray-700 px-3 py-2 rounded-md text-sm font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Dashboard
          </h1>
          <p className="text-gray-600">
            Manage your SEO campaigns and clients from one central location.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Clients</p>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? '...' : analytics?.overview.clients.total || 0}
                </p>
                <p className="text-xs text-green-600">
                  +{analytics?.overview.clients.recent || 0} this month
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart3 className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Campaigns</p>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? '...' : analytics?.overview.campaigns.active || 0}
                </p>
                <p className="text-xs text-gray-500">
                  {analytics?.overview.campaigns.total || 0} total
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Content Generated</p>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? '...' : analytics?.overview.content.total || 0}
                </p>
                <p className="text-xs text-green-600">
                  +{analytics?.overview.content.recent || 0} this month
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Globe className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Syndicated Posts</p>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? '...' : analytics?.overview.syndication.published || 0}
                </p>
                <p className="text-xs text-gray-500">
                  {analytics?.overview.syndication.jobs || 0} total jobs
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recent Campaigns */}
          <div className="lg:col-span-2">
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Recent Campaigns</h2>
                <button className="btn-secondary text-sm">View All</button>
              </div>
              
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((item) => (
                  <div key={item} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Globe className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Guest Post Campaign</p>
                        <p className="text-sm text-gray-600">Client: Acme Corp • Keywords: "digital marketing"</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Completed
                      </span>
                      <p className="text-sm text-gray-500 mt-1">2 hours ago</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="space-y-6">
            <div className="card">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <button 
                  onClick={handleCreateCampaign}
                  className="w-full btn-primary text-left flex items-center gap-3"
                >
                  <Plus className="w-5 h-5" />
                  Create Campaign
                </button>
                <button 
                  onClick={handleGenerateContent}
                  className="w-full btn-secondary text-left flex items-center gap-3"
                >
                  <FileText className="w-5 h-5" />
                  Generate Content
                </button>
                <button className="w-full btn-secondary text-left flex items-center gap-3">
                  <BarChart3 className="w-5 h-5" />
                  View Analytics
                </button>
                <button className="w-full btn-secondary text-left flex items-center gap-3">
                  <Settings className="w-5 h-5" />
                  Settings
                </button>
              </div>
            </div>

            <div className="card">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Usage This Month</h2>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Guest Posts</span>
                    <span className="font-medium">12 / 25</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: '48%' }}></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Syndication</span>
                    <span className="font-medium">34 / 50</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '68%' }}></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Citations</span>
                    <span className="font-medium">7 / 10</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full" style={{ width: '70%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* System Status */}
        <div className="mt-8">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">System Status</h2>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">All systems operational</span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 mb-1">301</div>
                <div className="text-sm text-gray-600">Active Domains</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 mb-1">99.9%</div>
                <div className="text-sm text-gray-600">Uptime</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600 mb-1">1.2s</div>
                <div className="text-sm text-gray-600">Avg Response</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
